import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Globe, MessageSquare, PlugZap } from "lucide-react";

import { webEntryService } from "@/entities/cs-web-entry";
import { useWeComModuleBound } from "@/entities/wecom-app/model/useWeComModuleBound";
import { KEFU_WEB_ENTRIES_QUERY_KEY, shouldShowNoChannel } from "../../model/kefuNoChannel";

/**
 * 客服舱「还没有接入任何客服渠道」中性提示(1010 一期补完 B1,替掉「需先绑定企业微信才能使用 AI 客服」)。
 * 企微客服、网页 / H5 是客服舱并列的两个渠道,哪个都不是前置条件。
 */
export function KefuNoChannelBanner({ enabled = true }: { enabled?: boolean }) {
  const { t } = useTranslation("customer-service");
  const wecomBound = useWeComModuleBound("customer_service");
  const entriesQ = useQuery({
    queryKey: KEFU_WEB_ENTRIES_QUERY_KEY,
    queryFn: webEntryService.list,
    enabled,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });
  const count = entriesQ.isSuccess ? (entriesQ.data?.length ?? 0) : undefined;
  if (!enabled || !shouldShowNoChannel(wecomBound, count)) return null;
  return (
    <div
      role="status"
      data-testid="kefu-no-channel"
      className="flex flex-wrap items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3"
    >
      <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground">{t("noChannel.title")}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{t("noChannel.desc")}</p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Link
          to="/channels?tab=web"
          data-testid="kefu-no-channel-web"
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Globe className="h-4 w-4" />
          {t("noChannel.createWeb")}
        </Link>
        <Link
          to="/channels?tab=wecom"
          data-testid="kefu-no-channel-wecom"
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
        >
          <MessageSquare className="h-4 w-4" />
          {t("noChannel.bindWecom")}
        </Link>
      </div>
    </div>
  );
}
